/**
 * Product Card Image Gallery
 * Handles hover-based image switching on desktop and swipe slider on mobile
 */
class CardProductGallery {
  constructor(element) {
    this.element = element;
    this.container = element.querySelector(".card-product-gallery__container");
    this.slides = Array.from(
      element.querySelectorAll(".card-product-gallery__slide")
    );
    this.thumbnails = Array.from(
      element.querySelectorAll(".card-product-gallery__thumbnail")
    );
    this.hoverAreas = Array.from(
      element.querySelectorAll(
        '.card-product-gallery__thumbnail[data-hover-area="true"]'
      )
    );

    this.currentIndex = 0;
    this.isMobile = window.innerWidth <= 749;
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.translateX = 0;
    this.slidesContainer = element.querySelector(
      ".card-product-gallery__slides"
    );

    this.init();
  }

  init() {
    if (this.slides.length <= 1) return;

    // Setup based on screen size and touch capability
    if (this.isMobile || this.isTouchDevice) {
      this.initMobileSlider();
    } else {
      this.initDesktopHover();
    }

    // Handle window resize
    window.addEventListener("resize", () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 749;

      if (wasMobile !== this.isMobile) {
        // Reinitialize if screen size changed
        this.currentIndex = 0;
        if (this.isMobile || this.isTouchDevice) {
          this.initMobileSlider();
        } else {
          this.initDesktopHover();
        }
      }
    });
  }

  initDesktopHover() {
    // Don't initialize hover on touch devices
    if (this.isTouchDevice) {
      return;
    }

    // Reset mobile styles
    if (this.slidesContainer) {
      this.slidesContainer.style.transform = "";
      this.container.classList.remove("is-dragging");
    }

    // Setup hover areas
    this.hoverAreas.forEach((area) => {
      const thumbnailIndex = parseInt(
        area.getAttribute("data-thumbnail-index") || "0",
        10
      );
      area.addEventListener("mouseenter", () => {
        this.showSlide(thumbnailIndex);
      });
    });

    // Reset to first slide when mouse leaves the container
    this.container.addEventListener("mouseleave", () => {
      this.showSlide(0);
    });

    // Show first slide
    this.showSlide(0);
  }

  initMobileSlider() {
    // Reset to first slide
    this.currentIndex = 0;
    this.translateX = 0;
    this.updateMobileTransform();

    // Disable hover areas on mobile
    this.hoverAreas.forEach((area) => {
      area.style.display = "none";
      area.style.pointerEvents = "none";
      area.style.visibility = "hidden";
    });

    // Setup touch events
    this.setupTouchEvents();

    // Show first slide
    this.showSlide(0);
  }

  setupTouchEvents() {
    if (!this.slidesContainer) return;

    const containerWidth = () => {
      return this.container.offsetWidth || this.slidesContainer.offsetWidth;
    };

    let initialTouchY = 0;
    let isVerticalScroll = false;

    // Touch start
    this.slidesContainer.addEventListener(
      "touchstart",
      (e) => {
        initialTouchY = e.touches[0].clientY;
        this.isDragging = true;
        this.startX = e.touches[0].clientX;
        this.currentX = this.startX;
        this.container.classList.add("is-dragging");
        isVerticalScroll = false;
      },
      { passive: true }
    );

    // Touch move
    this.slidesContainer.addEventListener(
      "touchmove",
      (e) => {
        if (!this.isDragging) return;

        const currentY = e.touches[0].clientY;
        const diffY = Math.abs(currentY - initialTouchY);
        const diffX = Math.abs(e.touches[0].clientX - this.startX);

        // If vertical movement is greater, allow page scroll
        if (diffY > diffX && diffY > 10) {
          isVerticalScroll = true;
          this.isDragging = false;
          this.container.classList.remove("is-dragging");
          return;
        }

        // Only handle horizontal swipe if not vertical scrolling
        if (!isVerticalScroll) {
          this.currentX = e.touches[0].clientX;
          const diff = this.currentX - this.startX;
          const width = containerWidth();
          const diffPercent = (diff / width) * 100;
          const newTranslateX = this.translateX + diffPercent;

          // Calculate boundaries
          const maxTranslate = 0;
          const minTranslate = -(this.slides.length - 1) * 100;

          // Apply transform with boundaries
          if (newTranslateX <= maxTranslate && newTranslateX >= minTranslate) {
            this.slidesContainer.style.transform = `translateX(${newTranslateX}%)`;
          }
        }
      },
      { passive: true }
    );

    // Touch end
    this.slidesContainer.addEventListener("touchend", () => {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.container.classList.remove("is-dragging");

      // Calculate which slide to snap to
      const width = containerWidth();
      const diff = this.currentX - this.startX;
      const diffPercent = Math.abs((diff / width) * 100);
      const threshold = 10; // Minimum swipe percentage (10% of container width)

      if (diffPercent > threshold) {
        if (diff > 0 && this.currentIndex > 0) {
          // Swipe right - go to previous
          this.currentIndex--;
        } else if (diff < 0 && this.currentIndex < this.slides.length - 1) {
          // Swipe left - go to next
          this.currentIndex++;
        }
      }

      // Update translateX and transform
      this.translateX = -this.currentIndex * 100;
      this.updateMobileTransform();
      this.updateThumbnails();
    });
  }

  updateMobileTransform() {
    if (this.slidesContainer) {
      this.slidesContainer.style.transform = `translateX(${this.translateX}%)`;
    }
  }

  showSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;

    // Update slides visibility
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add("card-product-gallery__slide--active");
      } else {
        slide.classList.remove("card-product-gallery__slide--active");
      }
    });

    // Update thumbnails
    this.updateThumbnails();

    // Update mobile transform
    if (this.isMobile) {
      this.translateX = -index * 100;
      this.updateMobileTransform();
    }
  }

  updateThumbnails() {
    this.thumbnails.forEach((thumbnail, index) => {
      if (index === this.currentIndex) {
        thumbnail.classList.add("card-product-gallery__thumbnail--active");
      } else {
        thumbnail.classList.remove("card-product-gallery__thumbnail--active");
      }
    });
  }
}

// Initialize all product card galleries
document.addEventListener("DOMContentLoaded", () => {
  const galleries = document.querySelectorAll(".card-product-image-gallery");
  galleries.forEach((gallery) => {
    new CardProductGallery(gallery);
  });
});

// Handle dynamically added cards
if (typeof MutationObserver !== "undefined") {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if the node itself is a gallery
          if (
            node.classList &&
            node.classList.contains("card-product-image-gallery")
          ) {
            new CardProductGallery(node);
          }
          // Check for galleries inside the node
          const galleries =
            node.querySelectorAll &&
            node.querySelectorAll(".card-product-image-gallery");
          if (galleries) {
            galleries.forEach((gallery) => {
              if (!gallery.dataset.galleryInitialized) {
                gallery.dataset.galleryInitialized = "true";
                new CardProductGallery(gallery);
              }
            });
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
